import classNames from 'classnames';
import { ReactElement, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import { useDebounce } from '../../hooks';
import './Tooltip.scss';

export type Position = 'left' | 'right' | 'top' | 'bottom';

interface Props {
  content: JSX.Element;
  children: ReactElement;
  className?: string;
  delay?: number;
}

export default function Tooltip({
  content,
  children,
  className = '',
  delay = 250,
}: Props) {
  const [isOpened, setIsOpened] = useState(false);
  const debouncedIsOpened = useDebounce(isOpened, delay);

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isShown = isOpened ? debouncedIsOpened : isOpened;
  const onMouseOver = () => {
    setIsOpened(true);
  };

  const onMouseOut = () => {
    setIsOpened(false);
  };

  return (
    <div
      className='Tooltip d-inline-block position-relative'
      ref={triggerRef}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
    >
      {children}
      <Transition
        in={isShown}
        unmountOnExit
        timeout={{
          exit: 250,
        }}
        nodeRef={contentRef}
      >
        <div
          className={classNames(
            'Tooltip__content position-absolute',
            className,
            {
              fadeIn: isShown,
              fadeOut: !isOpened,
            }
          )}
          ref={contentRef}
        >
          {content}
          <div className='Tooltip__arrow' />
        </div>
      </Transition>
    </div>
  );
}
